<template>
  <div
    class="documents-summary-item"
    :class="{ 'is-hovered': isItemHovered }"
    @mouseenter="onItemMouseEnter"
    @mouseleave="onItemMouseLeave"
  >
    <div
      class="documents-summary-item__title"
      @click="isDisabled ? '' : expandItem()"
      :class="{ disabled: isDisabled }"
    >
      <div>
        <span>{{ item.name }}</span>
      </div>
      <div class="documents-summary-item__right">
        <div class="documents-summary-item__actions" @click.prevent.stop>
          <BaseRoundButton
            v-if="item.type !== 'IMAGE' && item.type !== 'NUMBER'"
            :size="30"
            is-transparent
            :icon="aiIcon"
            :tooltip-text="$t(`evaluation-grid.field-use-in-extraction-${item.useInExtraction}`)"
            tooltipPlacement="top"
            @click.native="toggleEvaluationGridFieldUseInExtraction"
            @mouseenter.native="aiHovered = true"
            @mouseleave.native="aiHovered = false"
          />
          <BaseRoundButton
            :size="30"
            is-transparent
            :icon="pinIcon"
            :tooltip-text="pinTooltip"
            tooltipPlacement="top"
            @click.native="toggleEvaluationGridFieldPriority"
            @mouseenter.native="pinHovered = true"
            @mouseleave.native="pinHovered = false"
          />
          <BaseRoundButton
            v-if="tenantHasLLMExtractionsAccess() && item.type !== 'IMAGE' && item.type !== 'NUMBER'"
            :size="30"
            is-transparent
            icon="rotate-cw"
            :tooltip-text="regenerateTooltip"
            tooltipPlacement="top"
            :disabled="isExtracting"
            @click.native="regenerateField"
          />
          <BaseRoundButton
            :size="30"
            is-transparent
            icon="plus"
            :tooltip-text="$t('actions.add')"
            tooltipPlacement="top"
            @click.native="onAddClicked"
          />
        </div>
        <input
          v-if="item.type === 'IMAGE'"
          type="file"
          ref="imageFilePicker"
          multiple
          accept=".png,.jpeg,.jpg"
          class="documents-summary-item__hidden-input"
          @click.stop
          @change="onImageFilesPicked"
        />
        <BaseRoundButton
          v-if="!isDisabled && !isPinned"
          icon="chevron-down"
          is-transparent
          :size="30"
          class="documents-summary-item__chevron"
          :class="{ disabled: isDisabled, expanded }"
        />
      </div>
    </div>
    <FieldsExtractionProgress :fieldId="item.id" />
    <div v-if="searchingExtraction" class="documents-summary-item__searching">
      <BaseCircularLoadingBar :size="16" :width="2" />
      <span>{{ $t('documents-summary.searching-source') }}</span>
    </div>
    <div v-if="expanded">
      <div v-if="item.type === 'TEXT' || item.type === 'NUMBER'">
        <div
          v-for="(fieldValue, idx) in item.values"
          :key="`finding-${idx}`"
          @mouseover="() => hoverItem(idx)"
          @mouseleave="leaveItem"
          @click.stop
        >
          <div class="documents-summary-item__field" :class="{ hover: hoveredFindingIndex === idx }">
            <BaseExtraction
              :text="fieldValue.value"
              :method="fieldValue.source"
              :source="fieldValue.opportunityDocumentId"
              :evalGridValue="fieldValue"
              :isCompact="isCompact"
              @extraction-source-clicked="goToSource"
              @delete="onDeleteClicked"
              actionable
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="item.type === 'IMAGE' && (showAddInput || hasNoValues || expanded)"
      class="documents-summary-item__field"
    >
      <ImageFileUpload
        :images="images"
        :isUploading="isUploading"
        @add-files="uploadImages"
        @on-delete="onDeleteClicked"
      />
    </div>
    <BaseMenu
      ref="addItemSelect"
      :title="item.name"
      v-if="item.type === 'SELECT' && (showAddInput || hasNoValues || expanded)"
      :items="item.choices"
      :selection="item.values"
      :attach="`.documents-summary-item__add.documents-summary-item__values-container.select-${item.id}`"
      isMultiple
      idIsValue
      @add-item="addValueSelect"
      @remove-item="deleteValueSelect"
      @clear="clearValuesSelect"
    >
      <template slot="activator">
        <div :class="`documents-summary-item__add documents-summary-item__values-container select-${item.id}`">
          <div class="documents-summary-item__values">
            <div v-if="item.values.length === 0" class="documents-summary-item__no-value">
              {{ $t('evaluation-grid.no-value') }}
            </div>
            <BaseGridTag
              v-for="(value, index) in item.values"
              @click.native.stop="goToSource(value)"
              :key="index"
              :is-expanded="selectedValue && selectedValue.id === value.id"
              :is-actionable="value.textAnnotationId !== null"
              :value="value"
            >
            </BaseGridTag>
          </div>
          <BaseIcon class="documents-summary-item__key-icon" name="chevron-down" />
        </div>
      </template>
      <template v-slot:default="{ item }">
        <div>
          {{ item }}
        </div>
      </template>
    </BaseMenu>
    <div
      v-if="(item.type === 'TEXT' || item.type === 'NUMBER') && (showAddInput || hasNoValues)"
      class="documents-summary-item__add"
      :class="{ 'documents-summary-item__add--unsaved': unsavedInput }"
      @click.stop
    >
      <FormInput
        ref="addFormInput"
        :value="newInfoValue"
        @input="newInfoValue = $event"
        @blur="onAddInputBlur"
        @keydown.enter="onNewInfoEnter"
        :placeholder="addInfoPlaceholder"
        :type="item.type === 'NUMBER' ? 'number' : 'text'"
      />
      <span v-if="unsavedInput" class="documents-summary-item__add-hint">{{
        $t('documents-summary.unsaved-hint')
      }}</span>
    </div>
    <DocumentSummaryItemDeleteConfirmationPopup
      ref="confirmationDialog"
      :manipulated-object="manipulatedObject"
      @on-confirm="executeDelete(manipulatedObject)"
    />
  </div>
</template>

<script>
import { bus } from '@/main';
import { mapActions, mapGetters } from 'vuex';
import BaseIcon from '@/components/base/BaseIcon';
import BaseExtraction from '@/components/base/BaseExtraction.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseGridTag from '@/components/base/BaseGridTag.vue';
import BaseMenu from '@/components/base/BaseMenu.vue';
import FormInput from '@/components/form/FormInput.vue';
import DocumentSummaryItemDeleteConfirmationPopup from '@/components/page-opportunity-details/DocumentSummaryItemDeleteConfirmationPopup.vue';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import FieldsExtractionProgress from '@/components/page-opportunity-details/FieldsExtractionProgress.vue';
import { buildExtractionSearchQueries } from '@/util/extraction-search-query-builder';
import { tenantHasLLMExtractionsAccess } from '@/util/module-access-util';
import ImageFileUpload from '@/components/form/ImageFileUpload.vue';

export default {
  name: 'DocumentsSummaryItem',
  components: {
    BaseCircularLoadingBar,
    BaseIcon,
    BaseExtraction,
    BaseGridTag,
    BaseRoundButton,
    BaseMenu,
    FormInput,
    ImageFileUpload,
    DocumentSummaryItemDeleteConfirmationPopup,
    FieldsExtractionProgress,
  },
  props: {
    item: Object,
    isCompact: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOnAnalysisPage: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      expanded: false,
      isPinned: false,
      pinHovered: false,
      aiHovered: false,
      searchingExtraction: false,
      isItemHovered: false,
      hoverTimer: null,
      hoveredFindingIndex: null, // index of the finding being hovered
      newInfoValue: '',
      selectedValue: null,
      showAddInput: false,
      unsavedInput: false,
      manipulatedObject: null,
      isUploading: false,
    };
  },
  computed: {
    addInfoPlaceholder() {
      return this.$t('documents-summary.add-information');
    },
    hasNoValues() {
      return !this.item.values || this.item.values.length === 0;
    },
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('annotations', ['annotations', 'selectedAnnotation']),
    ...mapGetters('opportunityDocuments', ['opportunityDocument', 'opportunityDocuments']),
    ...mapGetters('pdfData', ['renderingCompleted']),
    ...mapGetters('fieldsExtraction', ['isExtracting', 'extractingFieldId', 'previousDocumentIds']),
    images() {
      return this.item.values.map((fieldValue) => ({
        url: fieldValue.sourceId,
        value: fieldValue.value,
        ...fieldValue,
      }));
    },
    isFieldExtracting() {
      return this.isExtracting && this.extractingFieldId === this.item.id;
    },
    regenerateTooltip() {
      return this.isExtracting ? this.$t('actions.extraction-in-progress') : this.$t('actions.ai-regenerate');
    },
    pinIcon() {
      return this.item.priority === 'LOW' ? 'pin-outline' : this.pinHovered ? 'pin-crossed' : 'pin';
    },
    pinTooltip() {
      return this.item.priority === 'LOW' ? this.$t('documents-summary.pin') : this.$t('documents-summary.unpin');
    },
    isDisabled() {
      return !this.item.values || this.item.values.length === 0;
    },
    aiIcon() {
      return !this.item.useInExtraction ? 'ai-outline' : this.aiHovered ? 'ai-unpin' : 'ai';
    },
  },
  methods: {
    tenantHasLLMExtractionsAccess,
    ...mapActions('evaluationGrid', ['updateEvalGridField', 'uploadEvalGridValueImage']),
    ...mapActions('fieldsExtraction', ['startExtraction']),
    ...mapActions('annotations', ['setSelectedAnnotation', 'resetSelectedAnnotation']),
    ...mapActions('currentDocument', ['scrollToAnnotation']),
    expandItem() {
      this.expanded = !this.expanded;
      this.showAddInput = this.hasNoValues ? this.showAddInput : false;
    },
    onItemMouseEnter() {
      this.hoverTimer = setTimeout(() => {
        this.isItemHovered = true;
      }, 50);
    },
    onItemMouseLeave() {
      clearTimeout(this.hoverTimer);
      this.isItemHovered = false;
    },
    regenerateField() {
      if (!this.opportunity || this.isExtracting) return;

      const hasPreviousSelection = this.previousDocumentIds.length > 0;

      this.startExtraction({
        opportunityId: this.opportunity.id,
        singleFieldId: this.item.id,
        documentIds: hasPreviousSelection ? this.previousDocumentIds : null,
        enableTriage: !hasPreviousSelection,
      });
    },
    addFreeText() {
      this.showAddInput = true;
      this.expanded = true;
      this.$nextTick(() => {
        const formInput = this.$refs.addFormInput;
        const input = formInput && formInput.$el && formInput.$el.querySelector('.form-input__input');
        if (input) input.focus();
      });
    },
    onAddClicked() {
      if (this.item.type === 'IMAGE') {
        this.selectImageFiles();
      } else {
        this.addFreeText();
      }
    },
    selectImageFiles() {
      if (this.$refs.imageFilePicker) this.$refs.imageFilePicker.click();
      this.expanded = true;
    },
    onImageFilesPicked(event) {
      const files = Array.from(event.target.files || []);
      if (files.length > 0) this.uploadImages(files);
      event.target.value = '';
    },
    onNewInfoEnter(event) {
      event.preventDefault();

      const value = this.newInfoValue.trim();
      if (!value) return;

      this.$emit('on-add', { fieldId: this.item.id, value });
      this.newInfoValue = '';
      this.unsavedInput = false;
      this.showAddInput = false;
    },
    onAddInputBlur() {
      if (!this.newInfoValue.trim()) {
        this.showAddInput = false;
        this.unsavedInput = false;
      } else {
        this.unsavedInput = true;
      }
    },
    addValueSelect(value) {
      this.$emit('on-add', { fieldId: this.item.id, value });
      this.showAddInput = false;
    },
    deleteValueSelect(value) {
      const currentValue = this.item.values.find((v) => v.value === value);
      this.executeDelete(currentValue);
    },
    clearValuesSelect() {
      const values = [...this.item.values];
      for (let i = 0; i < values.length; i++) {
        try {
          this.executeDelete(values[i]);
        } catch (error) {
          console.error('Failed to delete value:', error);
        }
      }

      //close menu
      this.$refs.addItemSelect.menu = false;
    },
    hoverItem(fieldValueIndex) {
      this.hoveredFindingIndex = fieldValueIndex;
    },
    leaveItem() {
      this.hoveredFindingIndex = null;
    },
    isSelectedAnnotation(fieldValue) {
      return fieldValue.textAnnotationId && fieldValue.textAnnotationId === this.selectedAnnotation?.id;
    },
    hasNoSource(fieldValue) {
      return fieldValue.source === 'NO_SOURCE' || !fieldValue.source;
    },
    goToSource(fieldValue) {
      // Checks if the value has a source and is not already selected (to avoid redundant navigation when clicking on the same value)
      if (this.hasNoSource(fieldValue) || this.isSelectedAnnotation(fieldValue)) {
        return;
      }

      this.handleDocumentLinks(fieldValue);
    },
    handleDocumentLinks(fieldValue) {
      if (fieldValue.source !== 'PDF_ANNOTATION') {
        this.resetSelectedAnnotation();
      }
      const page = this.getSourcePage(fieldValue);
      const extractionSearch =
        fieldValue.source === 'LLM_EXTRACTION' && fieldValue.value
          ? buildExtractionSearchQueries(fieldValue.value)
          : null;

      if (page === 0 && !extractionSearch) {
        return;
      }
      if (this.isOnAnalysisPage) {
        const emitSearch = () => {
          if (extractionSearch) {
            bus.$once('extraction-search-done', () => {
              this.searchingExtraction = false;
            });
            bus.$emit('search-extraction-source', { queries: extractionSearch, targetPage: page || null });
          } else if (page) {
            this.searchingExtraction = false;
            bus.$emit('scroll-to-page', page);
          }
        };

        if (this.opportunityDocument && this.opportunityDocument.id === fieldValue.opportunityDocumentId) {
          // Same document
          if (extractionSearch) {
            this.searchingExtraction = true;
            emitSearch();
          } else {
            this.searchingExtraction = false;
            this.navigateToAnnotation(page, fieldValue);
          }
        } else {
          // Different document
          const targetDoc = this.opportunityDocuments.find((doc) => doc.id === fieldValue.opportunityDocumentId);
          if (!targetDoc) return;

          if (extractionSearch) {
            this.searchingExtraction = true;
            bus.$emit('extraction-search-start');
            this.$nextTick(() => {
              bus.$once('pdf-viewer-ready', emitSearch);
              bus.$emit('change-current-document', targetDoc);
            });
          } else {
            this.searchingExtraction = false;
            bus.$emit('change-current-document', targetDoc);
            const unwatch = this.$watch(
              () => ({ renderingCompleted: this.renderingCompleted }),
              ({ renderingCompleted }) => {
                if (renderingCompleted) {
                  this.$nextTick(() => {
                    this.navigateToAnnotation(page, fieldValue);
                  });
                  unwatch();
                }
              },
              { immediate: true },
            );
          }
        }
      } else {
        // Guard: some extraction results have no identified source document
        // (opportunityDocumentId === null when Claude couldn't attribute the
        // answer). Navigating anyway would request /documents/null/file and
        // the backend would reject it as an invalid UUID.
        if (!fieldValue.opportunityDocumentId) return;
        bus.$emit('open-source-document', {
          documentId: fieldValue.opportunityDocumentId,
          page: page || undefined,
          fieldValueId: fieldValue.source === 'LLM_EXTRACTION' ? fieldValue.id : undefined,
        });
      }
    },
    navigateToAnnotation(page, fieldValue) {
      const foundAnnotation = this.annotations.find((a) => a.id === fieldValue.textAnnotationId) || null;

      if (foundAnnotation) {
        this.setSelectedAnnotation(foundAnnotation);
        bus.$emit('render-annotation-page', foundAnnotation);
        this.scrollToAnnotation({ page, annotation: foundAnnotation });
      } else {
        bus.$emit('scroll-to-page', page);
        this.resetSelectedAnnotation();
      }
    },
    toggleEvaluationGridFieldPriority() {
      const newPriority = this.item.priority === 'LOW' ? 'HIGH' : 'LOW';
      this.updateEvalGridField({
        opportunityId: this.opportunity.id,
        fieldId: this.item.id,
        newField: { ...this.item, priority: newPriority },
      })
        .then(() => {
          this.item.priority = newPriority;
          this.$toast.success(
            newPriority === 'LOW'
              ? this.$t('documents-summary.unpin-success')
              : this.$t('documents-summary.pin-success'),
          );
        })
        .catch(() => {
          this.$toast.error(this.$t('toast.error'));
        });
    },
    toggleEvaluationGridFieldUseInExtraction() {
      const newValue = !this.item.useInExtraction;
      this.updateEvalGridField({
        opportunityId: this.opportunity.id,
        fieldId: this.item.id,
        newField: { ...this.item, useInExtraction: newValue },
      })
        .then(() => {
          this.item.useInExtraction = newValue;
          this.$toast.success(this.$t('toast.success'));
        })
        .catch(() => {
          this.$toast.error(this.$t('toast.error'));
        });
    },
    getFileName(fieldValue) {
      if (!this.opportunity) {
        return '';
      }

      const document = this.opportunity.documents.find((doc) => doc.id === fieldValue.opportunityDocumentId);
      if (!document) {
        return '';
      }

      return document.name;
    },
    getSourcePage(fieldValue) {
      if (fieldValue.source === 'PDF_ANNOTATION') {
        const annotation = this.annotations.find((annotation) => annotation.id === fieldValue.textAnnotationId);
        if (!annotation) {
          return 0;
        }
        return annotation.startPage;
      } else {
        return fieldValue.opportunityDocumentPage ? fieldValue.opportunityDocumentPage : 0;
      }
    },
    onDeleteClicked(evalGridValue) {
      this.manipulatedObject = evalGridValue;
      this.$refs.confirmationDialog.openDialog();
    },
    executeDelete(value) {
      this.$emit('on-delete', { fieldId: value.evaluationGridFieldId, value });
      this.manipulatedObject = null;
    },
    uploadImages(files) {
      const filesArray = Array.from(files);
      let formData = new FormData();
      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        if (file) formData.append('file[]', file);
      }
      this.isUploading = true;
      this.uploadEvalGridValueImage({
        opportunityId: this.opportunity.id,
        fieldId: this.item.id,
        formData,
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
        })
        .catch((error) => {
          this.$toast.error(error, {}, this.$t('toast.error'));
        })
        .finally(() => {
          this.isUploading = false;
        });
    },
  },
  watch: {
    item: {
      immediate: true,
      deep: true,
      handler() {
        this.expanded = !this.expanded ? this.item.priority === 'HIGH' && !this.isDisabled : this.expanded;
        this.isPinned = this.item.priority === 'HIGH';
      },
    },
    hasNoValues(empty) {
      if (!empty) this.showAddInput = false;
    },
  },
  beforeDestroy() {
    clearTimeout(this.hoverTimer);
  },
};
</script>

<style lang="scss">
.documents-summary-item {
  outline: 1px solid transparent;
  padding: 4px 4px;
  margin-bottom: 4px;

  &__hidden-input {
    display: none;
  }

  .base-menu__activator {
    flex-grow: 1;
    width: 100%;
    padding: 4px;
  }

  &.is-hovered {
    background: $color-slate-100;
    outline: 1px solid $color-border;
    border-radius: $border-radius-root;

    .documents-summary-item__actions {
      opacity: 1;
    }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.15s ease;

    @include no-hover {
      opacity: 1;
    }
  }

  &__title {
    font-size: 16px;
    display: flex;
    align-items: center;
    user-select: none;
    justify-content: space-between;
    color: $color-normal;
    border-radius: 3px;
    transition: all 200ms ease;
    padding: 4px 6px 4px 4px;
    cursor: pointer;
    font-weight: bold;

    // Left section
    div:first-child {
      display: flex;
      align-items: center;
    }

    &.disabled {
      color: $color-normal-pale;
    }
  }

  &__field {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    border-radius: 3px;
    transition: all 200ms ease;
    width: 100%;
  }

  &__searching {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    color: $color-neutral;
    font-size: 13px;
  }

  &__info {
    color: $color-normal-pale;
    font-size: 12px;
    padding: 12px 16px;
  }

  &__add {
    padding: 4px;
    transition: background 0.2s ease, border-color 0.2s ease;

    .form-input {
      padding: 0;
    }

    .form-input__input {
      min-height: 36px;
      font-size: 14px !important;
    }

    &--unsaved {
      background-color: rgba($color-accent, 0.06);
      border-top-color: $color-accent;

      .form-input__input {
        border-color: $color-accent;
      }
    }

    &-hint {
      display: block;
      font-size: 12px;
      color: $color-neutral;
      margin-top: 4px;
    }
  }

  &__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;

    .base-round-button {
      transform: rotate(0deg);
      transform-origin: center center;
    }

    &.expanded .base-round-button {
      transform: rotate(180deg);
    }
  }

  &__values-container {
    position: relative;
    background: $color-white;
    color: $color-normal;
    border: 1px solid $color-neutral-pale;
    padding: 0 8px 0 0;
    display: flex;
    align-items: center;
    border-radius: $border-radius-small;
    flex: 1;
    width: 100%;
    min-height: 35px;

    flex-wrap: wrap;

    .base-icon {
      width: 16px;
      height: 16px;
    }

    &[class*='select'] {
      cursor: pointer;
      transition: all 200ms ease;
      display: flex;
      align-items: center;
    }

    &:hover {
      transition: all 200ms ease;
      border: 1px solid $color-normal;
    }
  }

  &__values {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
  }

  &__no-value {
    color: $color-neutral;
    display: flex;
    align-self: center;
    font-size: 14px;
    padding-left: 8px;
  }
}
</style>
